import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BRAND, SPREADS, TOPIC_LABELS, formatDate } from '@tarot/shared';
import { LogoMark } from '../../brand/BrandLogo';
import { getSharedReading } from '../../lib/api';
import { useTrack } from '../../lib/track';
import { slotGridClass, slotLabelClass } from '../../components/CardFan';
import { TarotCard } from '../../components/TarotCard';
import { TopicIcon } from '../../components/TopicIcon';
import { RichText } from '../../components/RichText';
import { CardMeaning } from '../../components/CardMeaning';
import { TossBanner } from '../../components/TossBanner';

/**
 * 공유된 운세 결과 (/r/:shareId) — 링크 받은 사람이 보는 화면.
 * 결과 전체(덱+해석)를 보여주고 "나도 타로 보러 가기" CTA로 서비스 유입.
 */
export function SharedReadingPage() {
  useTrack('view:shared');
  const { shareId } = useParams();
  const q = useQuery({
    queryKey: ['shared', shareId],
    queryFn: () => getSharedReading(shareId!),
    enabled: !!shareId,
    retry: 1,
  });

  if (q.isLoading) return <p className="py-16 text-center text-moon-dim">불러오는 중…</p>;

  if (q.isError || !q.data) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
        <p className="text-lg">공유된 결과를 찾을 수 없어요 🕯️</p>
        <Link
          to="/"
          className="flex h-12 w-full max-w-xs items-center justify-center gap-2 rounded-2xl bg-gold px-5 text-sm font-semibold text-night transition hover:bg-gold-deep"
        >
          <LogoMark className="h-6 w-6" /> 타로 보러 가기
        </Link>
      </div>
    );
  }

  const r = q.data;
  const spread = SPREADS[r.spreadId];
  const positionLabels = spread.positions.map((p) => p.nameKo);
  const topic = TOPIC_LABELS[r.topic];

  return (
    <div className="flex flex-1 flex-col gap-4">
      <div className="flex flex-col items-center gap-1.5">
        <div className="flex items-center gap-2.5 rounded-full border border-gold/30 bg-gold/10 px-5 py-2.5 text-base">
          <TopicIcon topic={r.topic} className="h-8 w-8 text-gold" />
          <span className="font-semibold">{topic.ko}</span>
          <span className="text-moon-dim">· {spread.nameKo}</span>
        </div>
        {/* 진행 날짜 + (커플·우정) 함께한 사람 */}
        <p className="text-center text-xs text-moon-dim">
          {formatDate(r.createdAt, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            weekday: 'short',
          })}
          {r.participants.length > 1 && (
            <> · 함께 본 사람: {r.participants.map((p) => p.nickname).join(' · ')}</>
          )}
        </p>
      </div>

      {r.participants.map((p) => (
        <section key={p.seat} className="flex flex-col gap-2">
          {r.participants.length > 1 && (
            <h3 className="text-lg font-semibold text-gold">{p.nickname}</h3>
          )}
          <ul className={`grid ${slotGridClass(spread.positions.length)}`}>
            {p.cards.map((d) => (
              <li key={d.position} className="flex min-w-0 flex-col items-center">
                <span className={slotLabelClass()}>{positionLabels[d.position]}</span>
                <TarotCard drawn={d} />
                <CardMeaning drawn={d} />
              </li>
            ))}
          </ul>
        </section>
      ))}

      <article className="whitespace-pre-wrap rounded-2xl border border-white/10 bg-white/5 p-5 leading-relaxed">
        <RichText text={r.interpretation} />
      </article>

      {/* 유입 CTA */}
      <div className="flex flex-col items-center gap-2 rounded-3xl border border-gold/25 bg-gold/5 p-5 text-center">
        <p className="flex items-center gap-2 font-semibold">
          <LogoMark className="h-8 w-8 text-gold" /> 나도 카드를 뽑아볼까?
        </p>
        <p className="text-sm text-moon-dim">{BRAND.description}</p>
        <Link
          to="/"
          className="mt-1 flex h-12 w-full max-w-xs items-center justify-center rounded-2xl bg-gold px-5 text-sm font-semibold text-night transition hover:bg-gold-deep"
        >
          무료로 타로 보러 가기
        </Link>
      </div>

      {/* 우리 CTA 아래에 둔다 — 광고가 유입 동선을 가리면 안 된다 */}
      <TossBanner />
    </div>
  );
}
